
import { LogisticsEmployeeService } from './logistics/employee/logistics-employee.service';
import { ShippingDocumentService } from 'src/app/core/services/shipping-document.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { LaasConstants } from './../constants/laas-constants.constant';
import { CacheService } from './cache.service';
import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { FacilityService } from './facility.service';
import { LaasSettingService } from './laas-setting.service';
import { EMPTY, Observable, of } from 'rxjs';
import { ExpenseService } from './expense.service';
import { GlobalConstants } from '../constants/global-constants.constant';
import { SkuService } from './sku.service';
import { DepartmentService } from './department.service';
import { UserService } from './user-service.service';
import { CommonDataService } from './common-data.service';

@Injectable({
  providedIn: 'root'
})
export class DropdownOptionsService {

  constructor(
    private expenseService: ExpenseService,
    private facilityService: FacilityService,
    private documentService: ShippingDocumentService,
    private laasSetting: LaasSettingService,
    private cacheService: CacheService,
    private skuService: SkuService,
    private departmentService: DepartmentService,
    private userService: UserService,
    private logisticsEmployeeService: LogisticsEmployeeService,
    private commonDataService : CommonDataService) {

  }

  /**
   * Get facility dropdown list options by country name.
   * @param countryName Country name
   * @param isDisplayWithValue Prefix with value
   */
  getFacilityOptions(countryName?: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getFacilityOptions(countryName);
    let isDefaultLanguage  = this.laasSetting.language == GlobalConstants.defaultLanguage
    if (isDefaultLanguage) {
      return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
        options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
        return options;
      })) : subject;
    } else {
      return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
        options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionEngDesc}`);
        return options;
      })) : subject;
    }
  }

  /**
   * Get forwarder dropdown list options by facility code.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getForwarderOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getForwarderOptions(facilityCode);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get forwarder dropdown list options by facility code.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getAllForwarderOptions(): Observable<any> {
    const subject = this.facilityService.getAllForwarderOptions();
    return subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    }))
  }

  /**
   * Get car seq dropdown list options by facility code.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getCarSeqOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getCarSeqOptions(facilityCode);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get car type dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getCarTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCarTypeOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get letter org dropdown list options.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getLetterOrgOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getLetterOrgOptions(facilityCode, '');
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.legalEntityOUName = `${x.legalEntityOUCode} - ${x.legalEntityOUName}`;
        x.billOUName = `${x.billOUCode} - ${x.billOUName}`;
      });
      return options;
    })) : subject;
  }

  /**
   * Get area dropdown list options.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getAreaOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getArea(facilityCode).pipe(map((options: any[]) => {
      return options.map(x => ({ optionDesc: x.rfqAreaName, optionCode: x.rfqAreaCode })) as any[];
    }));
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get forwarder account options by giving facility and forwarder name.
   * @param facilityCode Facility code
   * @param forwarderName Forwarder name
   * @param isDisplayWithValue Prefix with value
   */
  getAccountIdOptions(facilityCode: string, forwarderName: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getAccountIdOptions(facilityCode, forwarderName);
    return subject.pipe(
      map((options: any[]) => {
        const accountIdOptions = options.map(x => ({
          accountId: x.optionDesc,
          letterHeadOrgId: x.optionCode,
          optionCode: x.optionDesc,
          optionDesc: isDisplayWithValue ? `${x.optionCode} - ${x.optionDesc}` : x.optionDesc
        }));
        return accountIdOptions;
      }
      )
    );
  }

  /**
   * Get incoTerm dropdown list options
   * @param isDisplayWithValue Prefix with value
   */
  getIncoTermOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getIncoTermOption();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get pickup location dropdown list options by facility code.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getPickupLocationOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getPickupLocationOptions(facilityCode);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get document type dropdown list options by category.
   * @param category Document category
   */
  getDocumentTypeOptions(category?: number): Observable<any> {
    return this.documentService.getDocumentTypes(category).pipe(map((options: any[]) => {
      return options.map(x => {
        const option = { optionCode: x.docType, optionDesc: x.docNameEN, optionCategory: x.docCategory };
        switch (this.laasSetting.language) {
          case 'en-us':
            option.optionDesc = x.docNameEn;
            break;
          case 'zh-tw':
            option.optionDesc = x.docNameTw;
            break;
          case 'zh-cn':
            option.optionDesc = x.docNameCn;
            break;
          default:
            break;
        }
        return option;
      });
    }));
  }

  getDocumentTypeOptionsByCategories(category: number[]): Observable<any> {
    return this.documentService.getDocumentTypesByCategories(category).pipe(map((options: any[]) => {
      return options.map(x => {
        const option = { optionCode: x.docType, optionDesc: x.docNameEN, optionCategory: x.docCategor, isAllowRemovePrinted: x.isAllowRemovePrinted };
        switch (this.laasSetting.language) {
          case 'en-us':
            option.optionDesc = x.docNameEn;
            break;
          case 'zh-tw':
            option.optionDesc = x.docNameTw;
            break;
          case 'zh-cn':
            option.optionDesc = x.docNameCn;
            break;
          default:
            break;
        }
        return option;
      });
    }));
  }

  /**
   * Get carrier payment type dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getCarrierPaymentTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('CarrierPaymentType', isDisplayWithValue);
  }

  /**
   * Get charge dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getChargeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('ChargeType', isDisplayWithValue);
  }

  /**
   * Get booking type dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getBookingTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('BookingType', isDisplayWithValue);
  }

  /**
   * Get delivery type by giving Input/Output type.
   * @param isDisplayWithValue Prefix with value
   */
  getDeliveryTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('DeliveryType', isDisplayWithValue);
  }


  getRfqCategoryTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('RFQCategoryType', isDisplayWithValue);
  }


  /**
   * Get delivery sub-type by giving Input/Output type.
   * @param IOType Input/Output
   * @param isDisplayWithValue Prefix with value
   */
  getDeliverySubTypeOptions(IOType: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getDeliverySubTypeOptions(IOType);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  /**
   * Get DO booking status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getDOBookingStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('DoBookingStatus', isDisplayWithValue);
  }

  /**
   * Get booking status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getBookingStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('BookingStatus', isDisplayWithValue);
  }

  /**
   * Get DO status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getDOStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('DoStatus', isDisplayWithValue);
  }

  /**
   * Get Booking Detail status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getBookingDetailStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('BookingDetailStatus', isDisplayWithValue);
  }

  /**
   * Get priority dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getPriorityOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('Priority', isDisplayWithValue);
  }

  /**
   * Get pay type dropdown list options.
   * @param isDisplayWithValue
   */
  getPayTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('PayType', isDisplayWithValue);
  }

  /**
 *
 * @param isDisplayWithValue
 */
  getWarehousingShipTermOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('WarehousingShipTerm', isDisplayWithValue);
  }

  getWhDocStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('WhDocStatus', isDisplayWithValue);
  }


  getRFQStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('RFQStatus', isDisplayWithValue);
  }

  getRFQServiceTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('RFQServiceType', isDisplayWithValue);
  }


  /**
   * Get Leave warehouse status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getLeaveWarehouseStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('LeaveWarehouseStatus', isDisplayWithValue);
  }

  /**
   * Get booking status dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getChargesStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('BookingQueryChargesStatus', isDisplayWithValue);
  }


  /**
   * Get to country dropdown list options.
   * @param isDisplayWithValue Prefix with value
   */
  getToCountryOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCountryOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.countryName}-${x.countryEnName}`;
        x.optionCode = x.countryCode;
      });
      return options;
    })) : subject;
  }

  /**
   * Get letter head org, shipCorp, and org dropdown list options.
   * @param facilityCode Facility code
   * @param isDisplayWithValue Prefix with value
   */
  getOrgOptions(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getLetterOrgOptions(facilityCode, '');
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.legalEntityOUCode} - ${x.legalEntityOUName}`;
        x.optionCode = `${x.legalEntityOUName}`;
      });
      return options;
    })) : subject;
  }

  /**
   *
   * @param isDisplayWithValue
   */
  getCaseStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('CaseStatus', isDisplayWithValue);
  }

  getWhDetailStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('WhDetailStatus', isDisplayWithValue);
  }


  private fetchOptionsFromCache(subCategory: string, isDisplayWithValue: boolean): Observable<any> {
    const optionsGroup = this.cacheService.load(LaasConstants.dropdownListKey);

    if (isNullOrEmpty(optionsGroup)) {
      return EMPTY;
    }

    if (!optionsGroup.hasOwnProperty(subCategory)) {
      return EMPTY;
    }

    const optionValues =
      isNullOrEmpty(optionsGroup[subCategory]) ? [] :
        optionsGroup[subCategory].map((x: any) => ({ optionDesc: x.codeDesc, optionCode: x.codeKey }));
    const subject = of(optionValues);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }



  /**
   * Get pickup location dropdown list options
   * @param isDisplayWithValue Prefix with value
   */
  getCountryOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCountryOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.countryName = `${x.countryCode}-${x.countryName}-${x.countryEnName}`);
      return options;
    })) : subject;
  }


  /**
  * Get pickup location dropdown list options
  * @param isDisplayWithValue Prefix with value
  */
  getAllCityOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCityOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.name = `${x.name}-${x.enName}`);
      return options;
    })) : subject;
  }

  /**
* Get pickup location dropdown list options
* @param isDisplayWithValue Prefix with value
*/
  getAllAreaOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getAllAreaOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.rfqAreaName = `${x.rfqAreaCode}-${x.rfqAreaName}-${x.assignmentDistrictId}`);
      return options;
    })) : subject;
  }


  getAllAreaOptionForHK(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getAllAreaOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      var arr = [];
      for( let i = 0 ; i < options.length ; i++ ){
        var temp = options[i];
        var obj = {'rfqAreaCode' : temp.rfqAreaCode , 'rfqAreaName' : temp.rfqAreaCode+ '-'+temp.rfqAreaName , 'citySeqNo' : temp.citySeqNo};
        arr.push(obj);
      }
      const temp1 =   [...new Set(arr.map(o => JSON.stringify(o)))].map(s => JSON.parse(s));
      return temp1;
    })) : subject;
  }


  /**
* Get pickup location dropdown list options
* @param citySeq
* @param isDisplayWithValue Prefix with value
*/
  getAirPortOptions(citySeq: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getAirPortOptions(citySeq);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode}-${x.optionDesc}`);
      return options;
    })) : subject;
  }


  getBrandOptions(isActivated: Number, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getBrandOptions(isActivated);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.brandName = `${x.brandName}`);
      return options;
    })) : subject;
  }

  getFacilityOptionsByFacilityCode(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getFacilityOptionsByFacilityCode(facilityCode);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }



  getVCountryOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.expenseService.getVCountryOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.countryName = `${x.countryEnName}-${x.countryCode}-${x.countryName}`);
      return options;
    })) : subject;
  }


  getVCityOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.expenseService.getVCityOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.name = `${x.name}-${x.enName}`);
      return options;

    })) : subject;
  }


  getVAreaOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.expenseService.getVAreaOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      var arr = [];
      for( let i = 0 ; i < options.length ; i++ ){
        var temp = options[i];
        var obj = {'areaCode' : temp.areaCode , 'areaName' : temp.areaCode+ '-'+temp.areaName , 'city' : temp.city};
        arr.push(obj);
      }
      const temp2 =  [...new Map(arr.map(item =>[item['areaCode'], item])).values()];
      return temp2;
    })) : subject;
  }


  findFacilityOptionsByOptionType(optionType: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.expenseService.findFacilityOptionsByOptionType(optionType);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  findFeeTypeByFeeTypeCategory(optionType: string, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.expenseService.findFeeTypeByFeeTypeCategory(optionType);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.feeTypeName = `${x.feeTypeCode} - ${x.feeTypeName}`);
      return options;
    })) : subject;
  }





  getVendorOptions(key: string, isDisplayWithValue: boolean = true): Observable<any> {

    let tmepObj = { key: key };

    const subject = this.facilityService.getVendorOptions(tmepObj);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  getPickupLocationOptionByDefault(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getPickupLocationOptionByDefault(facilityCode);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  getDetailBillAccountCode(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getAccountOptions(facilityCode, "FLOW_BILL_TYPE");
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.optionCode} - ${x.optionDesc}`);
      return options;
    })) : subject;
  }

  getBillAccountCode(facilityCode?: string, isDisplayWithValue: boolean = true): Observable<any> {
    facilityCode ??= this.laasSetting.facility;
    const subject = this.facilityService.getAccountOptions(facilityCode, "FLOW_BILL_TYPE");
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x.optionDesc = `${x.commonAttr05} - ${x.commonAttr01}`);
      options.forEach(x => x.optionCode = `${x.commonAttr05}`);
      return options;
    })) : subject;
  }

  getShipToOptionsByConsigneeKey(params: any, isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getShipToOptionsByConsigneeKey(params);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = x.address;
        x.optionCode = x.siteUseId;
      });
      return options;
    })) : subject;
  }

  getCsrOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCsrOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.employeeNo} - ${x.employeeName}`;
        x.optionCode = x.email;
      });
      return options;
    })) : subject;
  }

  getSalesOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.logisticsEmployeeService.getSalesOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.employeeNo} - ${x.employeeName}`;
        x.optionCode = x.employeeNo;
      });
      return options;
    })) : subject;
  }

  getPcsrOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getPcsrOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.employeeNo} - ${x.employeeName}`;
        x.optionCode = x.employeeNo;
      });
      return options;
    })) : subject;
  }

  getPcsrAndCsrOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getPcsrAndCsrOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.employeeNo} - ${x.employeeName}`;
        x.optionCode = x.employeeNo;
      });
      return options;
    })) : subject;
  }
  //因getCsrOptions For 併單DO查詢頁面，optionCode為Email，所以加開拿工號的Function
  getCsrOptionsIsEmployeeCode(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.facilityService.getCsrOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.employeeNo} - ${x.employeeName}`;
        x.optionCode = x.employeeNo;
      });
      return options;
    })) : subject;
  }

  getTaskCodeOptions(isDisplayWithValue: boolean = true,category:string): Observable<any> {
    return this.facilityService.getTaskCodeOptions(category).pipe(
      switchMap((options: any[]) =>
        this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList').pipe(
          map((codeConverters: any[]) => {
            const converterMap = new Map(codeConverters.map(c => [c.codeKey, c.codeDesc]));

            const merged = options.map(opt => {
              const desc = converterMap.get(opt.taskCode) || opt.taskMeaning;
              return {
                ...opt,
                optionDesc: isDisplayWithValue ? `${opt.taskCode} - ${desc}` : desc,
                optionCode: opt.taskCode
              };
            });

            return merged;
          })
        )
      )
    );
  }



  getWaitingReferenceOption(isDisplayWithValue: boolean = true, facility: string): Observable<any> {
    const subject = this.facilityService.getWaitingReferenceOption(facility);
    return isDisplayWithValue ? subject.pipe(map((object: any) => {
      return object.wmsLovOptionList;
    })) : subject;
  }

  getVendorCategoryOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.skuService.getVendorCategoryOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      return options;
    })) : subject;
  }

  getDeptOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.departmentService.getDeptOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x = `${x}`);
      return options;
    })) : subject;
  }

  getUserOptions(isDisplayWithValue: boolean = true): Observable<any> {
    const subject = this.userService.getUserOptions();
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => x = `${x}`);
      return options;
    })) : subject;
  }

  getIQCFlagOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('IQCFlag', isDisplayWithValue);
  }

  getTaskCodeOptionsForDoSearch(isDisplayWithValue: boolean = true,category:string): Observable<any> {
    const subject = this.facilityService.getTaskCodeOptionsForDoSearch(category);
    return isDisplayWithValue ? subject.pipe(map((options: any[]) => {
      options.forEach(x => {
        x.optionDesc = `${x.taskCode} - ${x.taskMeaning}`;
        x.optionCode = x.taskCode;
      });
      return options;
    })) : subject;
  }

  getIsFeeCalculationOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('IsFeeCalculation', isDisplayWithValue);
  }

  getCBStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('CBStatus', isDisplayWithValue);
  }

  getHasCrossBoundryOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('HasCrossBoundry', isDisplayWithValue);
  }

  getPurgeStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('PurgeStatus', isDisplayWithValue);
  }

  getProcessStatusOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('PlantTransferProcessStatus', isDisplayWithValue);
  }

  getProcessFlagOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('PlantTransferProcessFlag', isDisplayWithValue);
  }

  getWhAbnormalTypeOptions(isDisplayWithValue: boolean = true): Observable<any> {
    return this.fetchOptionsFromCache('WhAbnormalType', false);
  }

}
